import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Minus } from "lucide-react";
import { api } from "../lib/api";

const GUIDE = {
  paper: { name: "Paper", best: "Dry goods, retail boxes, mailing, short-run promos", water: "Indoor / dry only", look: "Matte, semi-gloss or high-gloss white" },
  white_bopp: { name: "White BOPP", best: "Bottles, jars, cosmetics, fridge & freezer", water: "Waterproof, oil & tear resistant", look: "Bright white, vivid colour" },
  clear_bopp: { name: "Clear BOPP", best: "Glass & clear packaging — the 'no-label' look", water: "Waterproof, oil resistant", look: "Transparent; add white ink for pop" },
  gold: { name: "Gold metalized", best: "Premium spirits, candles, luxury gifting", water: "Moisture resistant", look: "Mirror gold base under full colour" },
  silver: { name: "Silver metalized", best: "Tech, cosmetics, chrome-look branding", water: "Moisture resistant", look: "Mirror silver base under full colour" },
  holographic: { name: "Holographic", best: "Limited editions, events, eye-catching shelf appeal", water: "Moisture resistant", look: "Rainbow shift under full colour" },
  felt: { name: "Felt / textured", best: "Wine, craft food, artisan & boutique brands", water: "Indoor / dry only", look: "Soft tactile paper, uncoated feel" },
  fluorescent: { name: "Fluorescent", best: "Warnings, sale tags, warehouse & attention labels", water: "Indoor / dry only", look: "Neon paper, single bright colour" },
  ul_polyester: { name: "UL polyester", best: "Electrical, equipment & compliance labels", water: "Chemical, heat & abrasion resistant", look: "Durable white polyester, UL recognised" },
};

const Yes = ({ ok }) => (ok ? <Check className="w-3.5 h-3.5 text-emerald-600 inline" /> : <Minus className="w-3.5 h-3.5 text-slate-300 inline" />);

const money = (n) => `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export function LabelStockGuide({ currentStock, spec }) {
  const [stocks, setStocks] = useState([]);
  const [live, setLive] = useState({});
  useEffect(() => { api.get("/pricing/label-stocks").then(({ data }) => setStocks(data.stocks || [])).catch(() => {}); }, []);
  const specKey = spec ? [spec.width_in, spec.height_in, spec.quantity, spec.shape, spec.lamination, spec.foil].join("|") : "";
  useEffect(() => {
    if (!spec || !Number(spec.width_in) || !Number(spec.height_in) || !Number(spec.quantity)) return undefined;
    const t = setTimeout(() => {
      api.post("/pricing/label-compare", {
        width_in: Number(spec.width_in), height_in: Number(spec.height_in), quantity: Number(spec.quantity),
        shape: spec.shape, lamination_name: spec.lamination, foil_name: spec.foil,
      }).then(({ data }) => setLive(Object.fromEntries((data.rows || []).map((r) => [r.stock, r])))).catch(() => setLive({}));
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [specKey]);
  if (!stocks.length) return null;
  const rows = stocks.filter((s) => GUIDE[s.key]);
  const current = live[currentStock];
  const switchHref = (slug) => {
    if (!spec) return `/shop/product/${slug}`;
    const q = new URLSearchParams();
    if (Number(spec.width_in) > 0) q.set("w", spec.width_in);
    if (Number(spec.height_in) > 0) q.set("h", spec.height_in);
    if (spec.quantity) q.set("qty", spec.quantity);
    if (spec.shape) q.set("shape", spec.shape);
    q.set("lam", spec.lamination || "");
    q.set("foil", spec.foil || "");
    if (GUIDE[currentStock]) q.set("from", GUIDE[currentStock].name);
    return `/shop/product/${slug}?${q.toString()}`;
  };
  const hasLive = Object.keys(live).length > 0;
  return (
    <section className="bg-white border border-slate-200" data-testid="label-stock-guide">
      <div className="px-6 pt-5 pb-3 flex flex-wrap items-end justify-between gap-2">
        <div>
          <div className="overline">Which label stock?</div>
          <h2 className="font-display font-bold text-base md:text-lg text-[#333F48] mt-1">Compare materials in 10 seconds</h2>
        </div>
        <p className="text-xs text-slate-500 max-w-md">Same print quality on every stock — pick by where the label lives. Prices update live for your current size, quantity and finishes.</p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead>
            <tr className="text-[10px] font-mono uppercase tracking-widest text-slate-500 text-left border-y border-slate-200">
              <th className="px-6 py-2">Stock</th><th className="py-2">Best for</th><th className="py-2">Durability</th>
              <th className="py-2 text-center">Lam.</th><th className="py-2 text-center">Foil</th><th className="py-2 text-right pr-6">{hasLive && spec ? `Price · ${spec.quantity?.toLocaleString?.() || spec.quantity} × ${spec.width_in}″×${spec.height_in}″` : "Price"}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((s) => {
              const g = GUIDE[s.key];
              const active = s.key === currentStock;
              return (
                <tr key={s.key} data-testid={`stock-guide-row-${s.key}`} className={`border-b border-slate-100 ${active ? "bg-[#DD6B33]/10" : "hover:bg-slate-50"} transition-colors duration-150`}>
                  <td className="px-6 py-2.5 align-top">
                    {active ? (
                      <span className="font-bold text-[#333F48]">{g.name} <span className="ml-1 text-[10px] font-mono uppercase text-[#DD6B33]">You're here</span></span>
                    ) : (
                      <Link to={switchHref(s.slug)} rel="nofollow" className="font-bold text-[#333F48] hover:text-[#DD6B33] inline-flex items-center gap-1" data-testid={`stock-guide-link-${s.key}`}>
                        {g.name} <ArrowRight className="w-3 h-3" />
                      </Link>
                    )}
                    <div className="text-[11px] text-slate-500">{g.look}</div>
                  </td>
                  <td className="py-2.5 pr-3 align-top text-slate-600">{g.best}</td>
                  <td className="py-2.5 pr-3 align-top text-slate-600">{g.water}</td>
                  <td className="py-2.5 text-center align-top"><Yes ok={s.lamination} /></td>
                  <td className="py-2.5 text-center align-top"><Yes ok={s.foil} /></td>
                  <td className="py-2.5 pr-6 text-right align-top font-mono text-slate-700 whitespace-nowrap" data-testid={`stock-guide-price-${s.key}`}>
                    {(() => {
                      const r = live[s.key];
                      if (!hasLive || !r) return s.key === "paper" ? "base" : `≈ ${s.vs_paper}× paper`;
                      if (!r.quotable) return <span className="text-slate-400 text-xs">Quote over {Number(r.quote_required_above).toLocaleString()}</span>;
                      const diff = current?.price != null ? r.price - current.price : null;
                      return (
                        <>
                          <div className="font-bold text-[#333F48]">{money(r.price)}</div>
                          {active ? <div className="text-[10px] text-[#DD6B33] uppercase">current</div>
                            : diff != null && <div className={`text-[11px] ${diff > 0 ? "text-slate-500" : "text-emerald-700"}`}>{diff > 0 ? "+" : diff < 0 ? "−" : ""}{money(Math.abs(diff))} vs this</div>}
                        </>
                      );
                    })()}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
